import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { productAPI, categoryAPI } from '../services/api';
import { Product, Category } from '../types';

const Products: React.FC = () => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [filterCategoryId, setFilterCategoryId] = useState<number | 'all'>('all');

  const { register, handleSubmit, reset, setValue, watch } = useForm<{
    name: string;
    price: number;
    message: string;
    categoryId: number;
  }>();

  // 상품 목록 조회
  const { data: products, isLoading } = useQuery({
    queryKey: ['products', sortBy, sortOrder],
    queryFn: async () => {
      console.log('정렬 요청:', { sortBy, sortOrder });
      const response = await productAPI.getAll({ sortBy, sortOrder });
      console.log('서버 응답:', response);
      return response.data || [];
    },
  });

  // 카테고리 목록 조회
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await categoryAPI.getAll();
      return response.data || [];
    },
  });

  // 상품 생성
  const createMutation = useMutation({
    mutationFn: productAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      handleClose();
    },
  });

  // 상품 수정
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Product> }) =>
      productAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      handleClose();
    },
  });

  // 상품 삭제
  const deleteMutation = useMutation({
    mutationFn: productAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });

  const handleOpen = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setValue('name', product.name);
      setValue('price', product.price);
      setValue('message', product.message || '');
      setValue('categoryId', product.category.id);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingProduct(null);
    reset();
  };

  const onSubmit = (data: any) => {
    if (editingProduct) {
      updateMutation.mutate({
        id: editingProduct.id,
        data: {
          ...data,
          price: Number(data.price),
        },
      });
    } else {
      createMutation.mutate({
        ...data,
        price: Number(data.price),
      });
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('정말로 삭제하시겠습니까?')) {
      deleteMutation.mutate(id);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
    }).format(price);
  };

  // 카테고리 필터(클라이언트) - '전체'면 모두 표시
  const visibleProducts = products?.filter(
    (p) => filterCategoryId === 'all' || p.category?.id === filterCategoryId,
  );

  const handleSortChange = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">상품 관리</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>카테고리</InputLabel>
            <Select
              value={filterCategoryId}
              label="카테고리"
              onChange={(e) => setFilterCategoryId(e.target.value as number | 'all')}
            >
              <MenuItem value="all">전체 카테고리</MenuItem>
              {categories?.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>정렬 기준</InputLabel>
            <Select
              value={sortBy}
              label="정렬 기준"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="id">ID</MenuItem>
              <MenuItem value="name">상품명</MenuItem>
              <MenuItem value="price">가격</MenuItem>
              <MenuItem value="createdAt">생성일</MenuItem>
              <MenuItem value="updatedAt">수정일</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>정렬 순서</InputLabel>
            <Select
              value={sortOrder}
              label="정렬 순서"
              onChange={(e) => setSortOrder(e.target.value as 'ASC' | 'DESC')}
            >
              <MenuItem value="ASC">오름차순</MenuItem>
              <MenuItem value="DESC">내림차순</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
          >
            상품 추가
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>상품명</TableCell>
              <TableCell>가격</TableCell>
              <TableCell>카테고리</TableCell>
              <TableCell>메시지</TableCell>
              <TableCell>상태</TableCell>
              <TableCell align="center">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleProducts?.map((product) => (
              <TableRow key={product.id}>
                <TableCell>{product.id}</TableCell>
                <TableCell>{product.name}</TableCell>
                <TableCell>{formatPrice(product.price)}</TableCell>
                <TableCell>{product.category.name}</TableCell>
                <TableCell>{product.message || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={product.isActive ? '활성' : '비활성'}
                    color={product.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton onClick={() => handleOpen(product)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(product.id)} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && !visibleProducts?.length && (
              <TableRow><TableCell colSpan={7} align="center">해당 카테고리 상품이 없습니다.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{editingProduct ? '상품 수정' : '상품 추가'}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="상품명"
              fullWidth
              required
              {...register('name', { required: true })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="가격"
              type="number"
              fullWidth
              required
              {...register('price', { required: true, min: 0 })}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>카테고리</InputLabel>
              <Select
                label="카테고리"
                value={watch('categoryId') || ''}
                {...register('categoryId', { required: true })}
              >
                {categories?.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              label="메시지"
              fullWidth
              multiline
              rows={3}
              {...register('message')}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>취소</Button>
            <Button type="submit" variant="contained">
              {editingProduct ? '수정' : '추가'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Products; 